import { skyState, upcomingZmanim } from './sky-state.js';
import { skyArtwork, SKY_HORIZON_BOTTOM } from './sky-artwork.js';
import { resolveScenery, SCENERY_OPTIONS } from './scenery-state.js';
import { createGuidanceControls } from './guidance-controls.js';

export function createSkyBanner(context) {
  const header = document.getElementById('topbar');
  const button = document.createElement('div');
  button.id = 'sky-banner'; button.className = 'sky-banner'; button.hidden = true;
  const skyButton = document.createElement('button');skyButton.type='button';skyButton.className='sky-open';
  skyButton.setAttribute('aria-label', 'Local sky and prayer times');
  const artMarkup = '<span class="sky-scenery"></span><span class="sky-celestial"></span>';
  skyButton.innerHTML = `<span class="sky-clock"><strong></strong><small></small></span><span class="sky-art" aria-hidden="true">${artMarkup}</span><span class="sky-next"><strong></strong><small></small></span>`;
  button.append(skyButton);
  const status=document.createElement('span');status.className='sky-status';status.hidden=true;
  header.append(button);
  const sheet = document.createElement('dialog');
  sheet.className = 'sky-sheet'; sheet.setAttribute('aria-labelledby', 'sky-title');
  sheet.innerHTML = '<header><h2 id="sky-title">Local sky &amp; times</h2><button type="button" aria-label="Close local sky">×</button></header><div class="sky-details"></div><fieldset><legend>Sky banner</legend><label><input type="checkbox" data-sky-pref="visible"> Show on Home and reader</label><label><input type="checkbox" data-sky-pref="motion"> Gentle transitions</label></fieldset><p class="sky-explanation">Calculated sky, not weather or observed visibility. Stars are illustrative; the panorama runs north–east–south–west–north. Moon shading is north-up. Prayer times follow the existing Baal HaTanya calendar, not the illustration.</p><footer><button type="button" data-sky-route="times">All halachic times</button><button type="button" data-sky-route="settings">Location settings</button></footer>';
  document.body.append(sheet);
  sheet.querySelector('.sky-details').after(status);
  const guidance = createGuidanceControls(context,button,sheet);
  const preview=document.createElement('div'); preview.className='sky-art sky-preview'; preview.setAttribute('aria-hidden','true'); preview.innerHTML=artMarkup;
  sheet.querySelector('header').after(preview);
  const sceneryLabel=document.createElement('label'); sceneryLabel.className='sky-scenery-control';
  sceneryLabel.innerHTML=`Scenery <select aria-label="Scenery" data-sky-scenery>${Object.entries(SCENERY_OPTIONS).map(([id,label])=>`<option value="${id}">${label}</option>`).join('')}</select>`;
  sheet.querySelector('fieldset').append(sceneryLabel);
  const sceneryInfo=document.createElement('p'); sceneryInfo.className='sky-scenery-info'; preview.after(sceneryInfo);
  sheet.querySelector('.sky-explanation').append(' Scenery is inspired by this area, not an exact view or current weather. Landscape silhouettes do not represent measured bearings or obstruct the calculated sun and moon. Regional scenery data: Natural Earth (public domain).');
  const escape = value => String(value ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c]));
  let prefs;
  try { prefs = JSON.parse(localStorage.getItem('siddur-sky') || '{}'); } catch { prefs = {}; }
  prefs = { visible: prefs?.visible !== false, motion: prefs?.motion !== false, scenery:Object.hasOwn(SCENERY_OPTIONS,prefs?.scenery||'')?prefs.scenery:'auto' };
  let sceneryKey,scenery;
  const artNodes=[button.querySelector('.sky-art'),preview];
  let currentSky;
  function draw(node) {
    const {width,height}=node.getBoundingClientRect();
    const drawing=skyArtwork(currentSky,width,height);
    node.style.setProperty('--sky-horizon-bottom',`${SKY_HORIZON_BOTTOM}px`);
    if(node.dataset.drawing===drawing)return;
    node.querySelector('.sky-celestial').innerHTML=drawing;
    node.dataset.drawing=drawing;
  }
  const artObserver=new ResizeObserver(entries=>entries.forEach(({target})=>draw(target)));
  artNodes.forEach(node=>artObserver.observe(node));
  function applyScenery(node,selection) {
    const holder=node.querySelector('.sky-scenery'),key=selection.assets.join('|');
    if(holder.dataset.key===key)return;
    holder.dataset.key=key; holder.replaceChildren();
    node.dataset.scene=selection.scene;
    // Decode before mounting: no broken-image glyphs and no layout shifts.
    Promise.all(selection.assets.map(src=>new Promise(resolve=>{
      const image=new Image();image.alt='';image.decoding='async';
      image.onload=()=>resolve({image,src});image.onerror=()=>resolve(null);image.src=src;
    }))).then(layers=>{
      if(holder.dataset.key!==key)return;
      if(layers.some(layer=>!layer)){node.dataset.scene='plain';node.dataset.sceneryUnavailable='true';return;}
      delete node.dataset.sceneryUnavailable;
      for(const {image,src} of layers){const layer=document.createElement('span');layer.className='sky-landscape-layer';layer.style.setProperty('--scenery-image',`url("${src}")`);layer.append(image);holder.append(layer);}
    });
  }
  let timer, route = 'home';
  const measure = () => {
    const height = Math.max(0, header.getBoundingClientRect().bottom);
    document.documentElement.style.setProperty('--reader-header-height', `${height}px`);
  };
  const observer = new ResizeObserver(measure); observer.observe(header);
  new MutationObserver(() => { measure(); update(); }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  const time = (date, tz) => new Intl.DateTimeFormat(undefined, { hour:'numeric', minute:'2-digit', ...(tz ? {timeZone:tz} : {}) }).format(date);
  function update() {
    clearTimeout(timer);
    button.hidden = !prefs.visible || !['home','reader','tehillim'].includes(route) || document.body.classList.contains('tehillim-focus');
    guidance.update();
    if ((!sheet.open && button.hidden) || document.visibilityState !== 'visible') { measure(); return; }
    const now = context.instant();
    const location = context.location();
    const nextSceneryKey=JSON.stringify([location?.latitude,location?.longitude,prefs.scenery]);
    if(nextSceneryKey!==sceneryKey){sceneryKey=nextSceneryKey;try{scenery=resolveScenery(location,prefs.scenery);}catch{scenery={scene:'plain',label:'Plain horizon',basis:'Regional classification unavailable',assets:[]};}}
    applyScenery(artNodes[0],scenery);
    if(sheet.open)applyScenery(preview,scenery);
    let sky, info, upcoming = [];
    if (location) {
      try { sky = skyState(now, location); } catch { /* Clock and zmanim survive an astronomy failure. */ }
      try {
        info = context.daily(now);
        upcoming = upcomingZmanim(now, info, () => {
          // First instant in the next civil date in the observer's timezone, DST-safe.
          const civil = d => new Intl.DateTimeFormat('en-CA', { timeZone: location.tzid }).format(d);
          const today = civil(now);
          for (let hours = 1; hours <= 26; hours++) {
            const next = new Date(+now + hours * 3600000);
            if (civil(next) !== today) return context.daily(next);
          }
          return null;
        });
      } catch { /* Polar dates may not have valid zmanim. */ }
    }
    button.dataset.lighting = sky?.lighting || 'unknown'; button.dataset.motion = String(prefs.motion);
    const below=sky ? [['Sun',sky.sun],['Moon',sky.moon]].filter(([,body])=>!body.aboveHorizon).map(([name])=>`${name} below horizon`) : [];
    const statusText=sky ? (below.join(' · ') || 'Sun and Moon above horizon') : '';
    status.textContent=statusText;status.hidden=!sky;
    sheet.dataset.lighting=button.dataset.lighting;sheet.dataset.motion=button.dataset.motion;
    button.querySelector('.sky-clock strong').textContent = time(now, location?.tzid);
    button.querySelector('.sky-clock small').textContent = location ? location.name : 'Set location';
    const art = button.querySelector('.sky-art');
    const previousDrawing=art.dataset.drawing;
    currentSky=sky;
    draw(art);
    if (art.dataset.drawing !== previousDrawing) {
      if (prefs.motion && !matchMedia('(prefers-reduced-motion: reduce)').matches && typeof art.animate === 'function') {
        art.animate([{opacity:.75},{opacity:1}], {duration:400, easing:'ease-out'});
      }
    }
    const next = upcoming[0];
    button.querySelector('.sky-next strong').textContent = next?.label || (location ? 'Local sky' : 'Location needed');
    const mins = next ? Math.max(1, Math.ceil((Date.parse(next.value) - +now) / 60000)) : 0;
    button.querySelector('.sky-next small').textContent = next ? `${time(new Date(next.value), location.tzid)} · ${mins >= 60 ? `${Math.floor(mins/60)}h ` : ''}${mins%60}m` : (sky ? sky.moon.phaseName : 'Tap for details');
    skyButton.setAttribute('aria-label', `Local sky and prayer times. ${time(now, location?.tzid)}. ${location?.name || 'Set location'}. ${statusText}.${next ? ` Next: ${next.label}, ${time(new Date(next.value), location.tzid)}.` : ''} Open details.`);
    if (sheet.open) {
      draw(preview);
      sceneryInfo.textContent=`${scenery.label} · ${scenery.basis}`;
      const bodyPosition = p => `${p.altitude.toFixed(1)}° elevation · ${p.azimuth.toFixed(0)}° bearing · ${p.aboveHorizon ? 'above' : 'below'} horizon`;
      sheet.querySelector('.sky-details').innerHTML = `<p><strong>${escape(location?.name || 'Location not set')}</strong><br>${escape(context.locationSource())}<br>${escape(new Intl.DateTimeFormat(undefined,{dateStyle:'full', timeStyle:'short', ...(location ? {timeZone:location.tzid} : {})}).format(now))}${location ? `<br>${escape(location.tzid)}` : ''}</p>${sky ? `<dl><dt>Sun</dt><dd>${bodyPosition(sky.sun)}</dd><dt>Moon · ${sky.moon.phaseName}</dt><dd>${bodyPosition(sky.moon)}<br>${Math.round(sky.moon.illumination*100)}% illuminated</dd></dl>` : '<p>Sky position unavailable. Set a location to calculate it offline.</p>'}<h3>Upcoming zmanim</h3>${upcoming.length ? `<dl>${upcoming.map(item => `<dt>${escape(item.label)}</dt><dd>${escape(time(new Date(item.value), location.tzid))}</dd>`).join('')}</dl>` : '<p>No valid upcoming times available for this location.</p>'}`;
    }
    measure();
    if (document.visibilityState === 'visible') timer = setTimeout(update, 60000 - Date.now()%60000 + 20);
  }
  function open() { sheet.showModal(); sheet.querySelectorAll('[data-sky-pref]').forEach(input => { input.checked = prefs[input.dataset.skyPref]; }); sheet.querySelector('[data-sky-scenery]').value=prefs.scenery; update(); }
  skyButton.addEventListener('click', open);
  sheet.querySelector('header button').addEventListener('click', () => sheet.close());
  sheet.addEventListener('close', update);
  sheet.addEventListener('change', event => {
    if(event.target.matches('[data-sky-scenery]'))prefs.scenery=Object.hasOwn(SCENERY_OPTIONS,event.target.value)?event.target.value:'auto';
    else if(event.target.dataset.skyPref)prefs[event.target.dataset.skyPref] = event.target.checked;
    else return;
    try { localStorage.setItem('siddur-sky', JSON.stringify(prefs)); } catch { /* Session preferences still work. */ }
    update();
  });
  sheet.addEventListener('click', event => {
    const target = event.target.closest('[data-sky-route]');
    if (target) { sheet.close(); context.navigate(target.dataset.skyRoute); }
  });
  document.addEventListener('visibilitychange', update);
  window.addEventListener('pageshow', update);
  window.addEventListener('siddur-native-resume', update);
  window.addEventListener('pagehide', () => clearTimeout(timer));
  return { update: kind => { if (kind) route = kind; update(); }, open, guidance };
}
